import { ISessionState, SessionState } from "./sessionState";
import { IndexedDbManager } from "../indexedDb/indexedDbManager";

//Session and SessionState is used in client and service worker, so do not import anything extra
export class Session {
    private static indexedDbAvailable: boolean = null;
    private static localStorageAvailable: boolean = null;

    private static isNullOrUndefined(test: any): boolean {
        return test === null || test === undefined;
    }

    private static isNullOrEmpty(test: string): boolean {
        return test === null || test === undefined || test === "";
    }

    static setIndexedDbAvailable(value: boolean) {
        Session.indexedDbAvailable = value;
        if (!value) {
            console.log("indexedDB IS NOT available");
        }
    }

    private static getIndexedDbAvailable(): boolean {
        if (!Session.isNullOrUndefined(Session.indexedDbAvailable)) return Session.indexedDbAvailable;
        try {
            Session.indexedDbAvailable = window !== null && window !== undefined && window.indexedDB !== null && window.indexedDB !== undefined;
        } catch (e) {
            Session.indexedDbAvailable = false;
        }
        return Session.indexedDbAvailable;
    }

    private static getLocalStorageAvailable(): boolean {
        if (!Session.isNullOrUndefined(Session.localStorageAvailable)) return Session.localStorageAvailable;
        try {
            Session.localStorageAvailable = localStorage !== null && localStorage !== undefined;
        } catch (e) {
            Session.localStorageAvailable = false;
        }
        return Session.localStorageAvailable;
    }

    static async setSessionState(sessionState: SessionState, setRefreshTime: boolean = true): Promise<void> {
        if (!Session.isNullOrUndefined(sessionState) && setRefreshTime) {
            sessionState.refreshTime = (new Date()).getTime();
        }
        if (Session.getLocalStorageAvailable()) {
            localStorage.setItem("S", !Session.isNullOrUndefined(sessionState) ? JSON.stringify(sessionState) : null);
        }
        if (Session.getIndexedDbAvailable()) {
            await IndexedDbManager.storeSessionState(sessionState);
        }
    }

    static getSessionState(): SessionState {
        if (Session.getLocalStorageAvailable()) {
            const str = localStorage.getItem("S");
            return !Session.isNullOrEmpty(str) ? new SessionState(JSON.parse(str)) : null;
        }
        return null;
    }

    static getSessionStateIndexedDb(): Promise<ISessionState> {
        if (Session.getIndexedDbAvailable()) {
            return IndexedDbManager.getSessionState();
        }
        return new Promise<ISessionState>((resolve) => { resolve(null); });
    }

    static getAuthenticatedUserCodeLocalStorage(): string {
        const sessionState = Session.getSessionState();
        return !Session.isNullOrUndefined(sessionState) ? sessionState.code : null;
    }

    static getAuthenticatedUserNameLocalStorage(): string {
        const sessionState = Session.getSessionState();
        return !Session.isNullOrUndefined(sessionState) ? sessionState.name : null;
    }

    static getAuthenticatedUserCode(): Promise<string> {
        return Session.getSessionStateIndexedDb().then((sessionState) => {
            return new Promise<string>((resolve) => { resolve(!Session.isNullOrUndefined(sessionState) ? sessionState.code : ""); });
        });
    }

    static getCompanyLogo(): Promise<string> {
        return Session.getSessionStateIndexedDb().then((sessionState) => {
            return new Promise<string>((resolve) => { resolve(!Session.isNullOrUndefined(sessionState) ? sessionState.companyLogo : ""); });
        });
    }

    static getCompanyName(): Promise<string> {
        return Session.getSessionStateIndexedDb().then((sessionState) => {
            return new Promise<string>((resolve) => { resolve(!Session.isNullOrUndefined(sessionState) ? sessionState.companyName : ""); });
        });
    }

    static getAccessToken(): string {
        const sessionState = Session.getSessionState();
        return !Session.isNullOrUndefined(sessionState) ? sessionState.accessToken : null;
    }

    static getRefreshToken(): string {
        const sessionState = Session.getSessionState();
        return !Session.isNullOrUndefined(sessionState) ? sessionState.refreshToken : null;
    }

    static getSignalRToken(): string {
        const sessionState = Session.getSessionState();
        let result = sessionState?.signalRToken;
        //Old session data may not have signalRToken
        if (!result && sessionState) {
            result = sessionState.refreshToken;
        }
        return result ?? "";
    }

    static tokenNeedsRefresh(): boolean {
        const sessionState = Session.getSessionState();
        if (Session.isNullOrUndefined(sessionState)) return false;
        const currentTime = (new Date()).getTime();
        const durationMin = (currentTime - sessionState.refreshTime) / (60 * 1000);
        return durationMin > 60; //Refresh token once in hour
        //console.log(durationMin, appConfig.authenticationTimeout);
        //return durationMin > (appConfig.authenticationTimeout / 2);
    }

    private static generateDeviceId(): string {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === "x" ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        }).toUpperCase();
    }

    private static storeDeviceId(deviceId: string) {
        if (Session.getLocalStorageAvailable()) {
            localStorage.setItem("deviceId", deviceId);
        }
    }

    static getDeviceId(): string {
        if (Session.getLocalStorageAvailable()) {
            let result = localStorage.getItem("deviceId");
            if (!result) {
                result = Session.generateDeviceId();
                Session.storeDeviceId(result);
            }
            return result;
        }
        return null;
    }
}
